import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Sparkles, RefreshCw, Loader2, Wand2 } from "lucide-react";
import { useBriefing, RiskRow, SEV_META } from "@/components/OpsBriefingCard";

const AREAS = { "": "All areas", installation: "Installations", scheduling: "Scheduling", sales: "Sales", warehouse: "Warehouse", support: "Support", system: "System health" };

function Chips({ value, onChange, options, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {options.map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k || "all"}`}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${value === k ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-indigo-300"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function OpsBriefing() {
  const [area, setArea] = useState("");
  const [showNotified, setShowNotified] = useState(false);
  const { data, denied, load } = useBriefing({ area: area || undefined, include_notified: showNotified });
  const [busy, setBusy] = useState("");
  const run = async (k, fn, ok) => { setBusy(k); try { const r = await fn(); if (ok) toast.success(ok(r.data)); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); } };
  if (denied) return <div className="p-8 text-sm text-slate-500" data-testid="ops-briefing-denied">The Operations Briefing is available to managers.</div>;
  if (data?.disabled) return <div className="p-8 text-sm text-slate-500" data-testid="ops-briefing-disabled">The Operations Briefing is not switched on for this company yet. An administrator can enable it in Admin Centre → Notifications &amp; Alerts.</div>;
  const items = data?.items || [];
  return (
    <div className="p-6 lg:p-8" data-testid="ops-briefing-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-slate-900"><Sparkles className="h-6 w-6 text-indigo-500" />Operations Briefing</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Problems found by TedOps rules in your live records, ranked by urgency. AI only explains and suggests; nothing is changed automatically.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => run("r", () => api.post("/ops-briefing/refresh"))} data-testid="ops-page-refresh" className="gap-1.5">
            {busy === "r" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
          </Button>
          <Button onClick={() => run("e", () => api.post("/ops-briefing/explain"), (d) => d.explained ? `${d.explained} recommendation(s) generated` : "No new AI recommendations (limit reached, AI off or nothing new)")}
            disabled={!data?.ai?.ai_enabled} className="gap-1.5 bg-indigo-600 text-white hover:bg-indigo-700" data-testid="ops-page-explain">
            {busy === "e" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} AI recommendations
          </Button>
        </div>
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <Chips value={area} onChange={setArea} options={Object.entries(AREAS).filter(([k]) => !k || (data?.areas || []).includes(k))} testid="ops-area" />
        <label className="flex items-center gap-2 text-xs text-slate-600">
          <input type="checkbox" checked={showNotified} onChange={(e) => setShowNotified(e.target.checked)} className="accent-indigo-600" data-testid="ops-show-notified" />
          Include {data?.already_notified || 0} item(s) already in Notifications
        </label>
      </div>
      {!data ? <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="mt-6 grid gap-6 xl:grid-cols-2">
          {Object.entries(SEV_META).map(([k, m]) => {
            const xs = items.filter((r) => r.severity === k);
            return (
              <section key={k} data-testid={`ops-section-${k}`}>
                <h2 className={`mb-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${m.cls}`}><m.icon className="h-3.5 w-3.5" />{m.label} {"\u00b7"} {xs.length}</h2>
                <div className="space-y-2">
                  {xs.map((r) => <RiskRow key={r.id} r={r} onAck={(id) => run(`a${id}`, () => api.post(`/ops-briefing/${id}/acknowledge`))} />)}
                  {xs.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">Nothing here.</div>}
                </div>
              </section>
            );
          })}
        </div>
      )}
      {data?.refreshed_at && <p className="mt-6 text-xs text-slate-400" data-testid="ops-refreshed-at">Checked {new Date(data.refreshed_at).toLocaleTimeString("en-GB")} {"\u00b7"} AI {data.ai?.ai_enabled ? `on, up to ${data.ai.ai_daily_limit} analyses a day` : "off"}</p>}
    </div>
  );
}
